import { useState } from 'react';
import { useSupabaseAuth } from './hooks/useSupabaseAuth';
import { useLivePhotos } from './hooks/useLivePhotos';
import { uploadPhotoPipeline } from './lib/upload';
import { deleteMissingGpsPhoto } from './lib/deletePhoto';
import { AppScenario, ScenarioState } from './data/mock-data';
import { UploadPanel, type UploadSource } from './components/UploadPanel';
import { MapSurface } from './components/MapSurface';
import { DetailSheet } from './components/DetailSheet';
import { DateRangeFilter, isPhotoInDateRange } from './lib/photoFilters';
import type { PhotoStatus } from './types/contract';

const getUploadSuccessMessage = (
  status: PhotoStatus | null,
  resetDateFilter: boolean,
  source: UploadSource,
  browserFileHadReadableGps: boolean,
) => {
  const suffix = resetDateFilter ? ' 새로 등록한 사진을 바로 보도록 기간 필터를 초기화했습니다.' : '';

  if (status === 'ready') {
    const sourceLabel = source === 'camera' ? '촬영한 사진' : '선택한 사진';
    const gpsNote = browserFileHadReadableGps
      ? `${sourceLabel} 파일에서 위치를 확인했습니다.`
      : `${sourceLabel}은 서버에서 위치가 확인됐지만 브라우저 사전 판정에서는 위치를 읽지 못했습니다.`;

    return `등록이 끝났습니다. ${gpsNote} 지도에서 확인할 수 있습니다.${suffix}`;
  }

  if (status === 'missing_gps') {
    if (source === 'camera') {
      return `사진은 등록됐지만 방금 촬영해 브라우저가 받은 파일 안에서 지도 위치를 찾지 못했습니다. 휴대폰 카메라 위치 권한과 브라우저의 사진 전달 방식을 확인해 주세요.${suffix}`;
    }

    return `사진은 등록됐지만 브라우저가 받은 기존 사진 파일 안에서 지도 위치를 찾지 못했습니다. 사진 앱에는 위치가 보여도 선택 과정에서 위치 메타데이터가 빠진 복사본이 전달될 수 있습니다.${suffix}`;
  }

  if (status === 'failed') {
    return `사진은 저장됐지만 등록 또는 위치 판정을 완료하지 못했습니다. 처리 실패 목록에서 확인하거나 다시 선택하세요.${suffix}`;
  }

  return `등록이 끝났습니다. 사진 목록에서 처리 결과를 확인하세요.${suffix}`;
};

export function LiveApp() {
  const { session, signOut } = useSupabaseAuth();
  const { photos, jobs, loading, error, refresh } = useLivePhotos();
  
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRangeFilter>({ start: '', end: '' });
  const [uploadState, setUploadState] = useState<{ status: ScenarioState; helper: string }>({
    status: 'ready',
    helper: '현장 사진을 선택하면 GPS, 촬영 시각, 압축 미리보기를 준비합니다.',
  });
  const [lastFailedUpload, setLastFailedUpload] = useState<{ files: File[]; source: UploadSource } | null>(null);
  const [deletingPhotoId, setDeletingPhotoId] = useState<string | null>(null);

  const handleSelectPhoto = (id: string) => {
    setSelectedPhotoId(id);
  };

  const handleUpload = async (files: File[], source: UploadSource = 'library') => {
    if (files.length === 0) return;
    setLastFailedUpload(null);
    setUploadState({
      status: 'loading',
      helper: source === 'camera'
        ? '촬영한 현장 사진에서 GPS를 확인하고 압축해 서버에 등록하고 있습니다.'
        : `${files.length}장의 사진에서 GPS를 확인하고 등록하고 있습니다.`,
    });

    let successCount = 0;
    let failCount = 0;
    let lastError: any = null;
    let lastResult: any = null;
    const failedFiles: File[] = [];

    for (const file of files) {
      try {
        const result = await uploadPhotoPipeline(file, {
          preserveOriginal: source === 'library',
        });
        successCount++;
        lastResult = result;
      } catch (err: any) {
        failCount++;
        lastError = err;
        failedFiles.push(file);
      }
    }

    if (failedFiles.length > 0) {
      setLastFailedUpload({ files: failedFiles, source });
    }

    const resetDateFilter = Boolean(dateRange.start || dateRange.end);
    if (resetDateFilter) {
      setDateRange({ start: '', end: '' });
    }

    if (failCount === 0) {
      setUploadState({
        status: 'ready',
        helper: files.length === 1 && lastResult
          ? getUploadSuccessMessage(lastResult.photoStatus, resetDateFilter, source, lastResult.browserFileHadReadableGps)
          : `총 ${files.length}장의 사진을 성공적으로 등록했습니다.${resetDateFilter ? ' 새로 등록한 사진을 바로 보도록 기간 필터를 초기화했습니다.' : ''}`,
      });
      if (lastResult) setSelectedPhotoId(lastResult.photoId);
    } else if (successCount > 0) {
      setUploadState({
        status: 'error',
        helper: `총 ${files.length}장 중 ${successCount}장 성공, ${failCount}장 실패했습니다. 마지막 실패 원인: ${lastError?.message}`,
      });
      if (lastResult) setSelectedPhotoId(lastResult.photoId);
    } else {
      setUploadState({
        status: 'error',
        helper: `최근 실패 원인: ${lastError?.message || '알 수 없는 오류가 발생했습니다.'} 다시 시도하거나 다른 원본 사진을 선택하세요.`,
      });
    }

    await refresh();
  };

  const handleRetryUpload = () => {
    if (lastFailedUpload) {
      void handleUpload(lastFailedUpload.files, lastFailedUpload.source);
    }
  };

  const handleDeleteMissingGpsPhoto = async (photoId: string) => {
    const targetPhoto = photos.find((photo) => photo.id === photoId);
    if (!targetPhoto || (targetPhoto.status !== 'missing_gps' && targetPhoto.status !== 'failed')) return;

    const confirmed = window.confirm(
      targetPhoto.status === 'failed'
        ? '지도에 표시되지 않은 사진을 삭제할까요? 같은 원본은 다시 선택해 등록할 수 있습니다.'
        : '위치 없는 사진을 삭제할까요? 삭제 후에는 이 화면에서 복구할 수 없습니다.',
    );
    if (!confirmed) return;

    setDeletingPhotoId(photoId);
    try {
      await deleteMissingGpsPhoto(photoId);
      if (selectedPhotoId === photoId) {
        setSelectedPhotoId(null);
      }
      await refresh();
    } catch (err: any) {
      window.alert(err.message || '사진 삭제에 실패했습니다.');
    } finally {
      setDeletingPhotoId(null);
    }
  };

  // Derive scenario status
  let overallStatus: ScenarioState = 'ready';
  if (loading) overallStatus = 'loading';
  else if (error) overallStatus = 'error';
  else if (photos.length === 0) overallStatus = 'empty';

  const filteredPhotos = photos.filter((photo) => isPhotoInDateRange(photo, dateRange));
  const isFilteredEmpty = Boolean(dateRange.start || dateRange.end) && photos.length > 0 && filteredPhotos.length === 0;

  const scenarioState: AppScenario = {
    upload: uploadState,
    map: { status: overallStatus },
    detail: { status: overallStatus },
    photos: filteredPhotos,
    jobs,
    selectedPhotoId,
  };

  const activePhotoId = filteredPhotos.some((p) => p.id === selectedPhotoId)
    ? selectedPhotoId
    : filteredPhotos[0]?.id ?? null;

  return (
    <div className="app-shell">
      <div className="shell-frame">
        <header className="shell-header">
          <div className="shell-header__main">
            <div>
              <p className="shell-header__eyebrow">굴착공사사진 확인</p>
              <h1 className="shell-header__title">굴착공사사진 확인</h1>
              <p className="shell-header__subtitle">
                {session?.user?.email} 계정의 현장 사진 기록입니다. GPS와 촬영 시각으로 지중시설 이력을 지도에서 확인합니다.
              </p>
            </div>
            <button
              onClick={signOut}
              className="button button--secondary shell-header__logout"
            >
              로그아웃
            </button>
          </div>
          <UploadPanel
            scenarioState={scenarioState}
            rootScenario={overallStatus}
            onScenarioChange={() => {}}
            onSelectPhoto={handleSelectPhoto}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            onDateRangeReset={() => setDateRange({ start: '', end: '' })}
            onUpload={handleUpload}
            onRetryUpload={handleRetryUpload}
            canRetryUpload={Boolean(lastFailedUpload) && uploadState.status === 'error'}
            onDeletePhoto={handleDeleteMissingGpsPhoto}
            deletingPhotoId={deletingPhotoId}
            isFilteredEmpty={isFilteredEmpty}
            mode="live"
            showScenarioControls={false}
            variant="top"
          />
        </header>

        <div className="shell-layout">
          <MapSurface 
            scenarioState={scenarioState} 
            onSelectPhoto={handleSelectPhoto} 
            selectedPhotoId={activePhotoId} 
            onRetry={() => void refresh()}
            isFilteredEmpty={isFilteredEmpty}
          />
          <DetailSheet 
            scenarioState={scenarioState} 
            onSelectPhoto={handleSelectPhoto} 
            selectedPhotoId={activePhotoId} 
            onRetry={() => void refresh()}
            onDeletePhoto={handleDeleteMissingGpsPhoto}
            deletingPhotoId={deletingPhotoId}
            isFilteredEmpty={isFilteredEmpty}
          />
        </div>
      </div>
    </div>
  );
}
